import { api } from "./api";

/**
 * Saved OAuth app config — the user-supplied client_id/secret for an
 * OAuth provider (e.g. "google"). One app per (project, provider) —
 * it powers every integration that rides that provider (Gmail,
 * Calendar, Drive, Analytics, …) so the user only pastes secrets once.
 * Secrets never leave the server; the read APIs return a preview plus
 * the callback URL the user needs to register in their developer
 * console.
 */
export interface OauthApp {
  id: string;
  provider: string;
  name: string;
  /** First-N + last-4 of the client id so the UI can identify which app this is. */
  clientIdPreview: string;
  /** Where the upstream OAuth provider must redirect after consent. */
  callbackUrl: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertOauthAppInput {
  provider: string;
  name: string;
  clientId: string;
  clientSecret: string;
}

export interface AuthProvider {
  /** Canonical provider key (e.g. `"google"`). */
  key: string;
  /** Display name (`"Google"`). */
  name: string;
  /** Public URL the SPA can drop into `<img src>` directly. */
  logo: string;
  /** Authorize endpoint, surfaced for informational UI. */
  authorizeUrl: string;
  scopes: string[];
  /** Where the upstream OAuth provider must redirect after consent. */
  callbackUrl: string;
}

export const oauthAppsApi = {
  list: (provider?: string) =>
    api<OauthApp[]>(
      provider
        ? `/v1/oauth-apps?provider=${encodeURIComponent(provider)}`
        : `/v1/oauth-apps`,
    ),
  providers: () => api<AuthProvider[]>("/v1/oauth-apps/providers"),
  upsert: (input: UpsertOauthAppInput) =>
    api<OauthApp>("/v1/oauth-apps", { method: "POST", json: input }),
  remove: (id: string) =>
    api<{ id: string; deleted: true }>(`/v1/oauth-apps/${id}`, {
      method: "DELETE",
    }),
};
